import { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Paper,
  Typography,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  IconButton,
  Chip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  FormControlLabel,
  Switch,
  Tooltip,
  Alert,
  Snackbar,
  CircularProgress,
  Autocomplete,
  InputAdornment,
} from '@mui/material';
import {
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Refresh as RefreshIcon,
  Visibility as VisibilityIcon,
  VisibilityOff as VisibilityOffIcon,
  ContentCopy as CopyIcon,
  Key as KeyIcon,
  CheckCircle as ActiveIcon,
  Cancel as InactiveIcon,
} from '@mui/icons-material';
import { useConfirm } from 'material-ui-confirm';
import { BrowserTitle } from '../components';
import apiClientServices from '../services/apiClientServices';

const formatDate = (date) => {
  if (!date) return 'Never';
  return new Date(date).toLocaleString();
};

const ApiIntegrations = () => {
  const confirm = useConfirm();
  const [clients, setClients] = useState([]);
  const [loading, setLoading] = useState(true);
  const [availableScopes, setAvailableScopes] = useState([]);
  
  // Dialog state
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingClient, setEditingClient] = useState(null);
  const [formData, setFormData] = useState({
    name: '',
    description: '',
    ipWhitelist: [],
    scopes: [],
    isActive: true,
  });
  const [ipInput, setIpInput] = useState('');
  
  // Keys dialog
  const [keysDialogOpen, setKeysDialogOpen] = useState(false);
  const [generatedKeys, setGeneratedKeys] = useState({ apiKey: '', secretKey: '' });
  const [showSecretKey, setShowSecretKey] = useState(false);
  
  // Snackbar
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });

  const loadClients = useCallback(async () => {
    try {
      setLoading(true);
      const response = await apiClientServices.getAll();
      setClients(response.data || []);
    } catch (error) {
      console.error('Error loading API clients:', error);
      setSnackbar({ open: true, message: 'Failed to load API clients', severity: 'error' });
    } finally {
      setLoading(false);
    }
  }, []);

  const loadScopes = useCallback(async () => {
    try {
      const response = await apiClientServices.getAvailableScopes();
      setAvailableScopes(response.data || []);
    } catch (error) {
      console.error('Error loading scopes:', error);
    }
  }, []);

  useEffect(() => {
    loadClients();
    loadScopes();
  }, [loadClients, loadScopes]);

  const handleOpenDialog = (client = null) => {
    if (client) {
      setEditingClient(client);
      setFormData({
        name: client.name || '',
        description: client.description || '',
        ipWhitelist: client.ipWhitelist || [],
        scopes: client.scopes || [],
        isActive: client.isActive ?? true,
      });
    } else {
      setEditingClient(null);
      setFormData({
        name: '',
        description: '',
        ipWhitelist: [],
        scopes: [],
        isActive: true,
      });
    }
    setIpInput('');
    setDialogOpen(true);
  };

  const handleCloseDialog = () => {
    setDialogOpen(false);
    setEditingClient(null);
    setFormData({
      name: '',
      description: '',
      ipWhitelist: [],
      scopes: [],
      isActive: true,
    });
    setIpInput('');
  };

  const handleSubmit = async () => {
    try {
      if (!formData.name.trim()) {
        setSnackbar({ open: true, message: 'Name is required', severity: 'error' });
        return;
      }

      if (editingClient) {
        await apiClientServices.update(editingClient.id, formData);
        setSnackbar({ open: true, message: 'API client updated successfully', severity: 'success' });
      } else {
        const response = await apiClientServices.create(formData);
        // Show the generated keys
        setGeneratedKeys({
          apiKey: response.data.apiKey,
          secretKey: response.data.secretKey,
        });
        setKeysDialogOpen(true);
        setSnackbar({ open: true, message: 'API client created successfully', severity: 'success' });
      }

      handleCloseDialog();
      loadClients();
    } catch (error) {
      console.error('Error saving API client:', error);
      setSnackbar({ open: true, message: error.response?.data?.message || 'Failed to save API client', severity: 'error' });
    }
  };

  const handleDelete = async (client) => {
    try {
      await confirm({
        title: 'Delete API Client',
        description: `Are you sure you want to delete "${client.name}"? This action cannot be undone.`,
        confirmationText: 'Delete',
        confirmationButtonProps: { color: 'error' },
      });

      await apiClientServices.remove(client.id);
      setSnackbar({ open: true, message: 'API client deleted successfully', severity: 'success' });
      loadClients();
    } catch (error) {
      if (error !== undefined) {
        console.error('Error deleting API client:', error);
        setSnackbar({ open: true, message: 'Failed to delete API client', severity: 'error' });
      }
    }
  };

  const handleToggleActive = async (client) => {
    try {
      await apiClientServices.toggleActive(client.id);
      setSnackbar({ 
        open: true, 
        message: `API client ${client.isActive ? 'disabled' : 'enabled'} successfully`, 
        severity: 'success' 
      });
      loadClients();
    } catch (error) {
      console.error('Error toggling API client status:', error);
      setSnackbar({ open: true, message: 'Failed to toggle status', severity: 'error' });
    }
  };

  const handleRegenerateKeys = async (client) => {
    try {
      await confirm({
        title: 'Regenerate API Keys',
        description: `Are you sure you want to regenerate keys for "${client.name}"? The old keys will stop working immediately.`,
        confirmationText: 'Regenerate',
        confirmationButtonProps: { color: 'warning' },
      });

      const response = await apiClientServices.regenerateKeys(client.id);
      setGeneratedKeys({
        apiKey: response.data.apiKey,
        secretKey: response.data.secretKey,
      });
      setKeysDialogOpen(true);
      setSnackbar({ open: true, message: 'Keys regenerated successfully', severity: 'success' });
      loadClients();
    } catch (error) {
      if (error !== undefined) {
        console.error('Error regenerating keys:', error);
        setSnackbar({ open: true, message: 'Failed to regenerate keys', severity: 'error' });
      }
    }
  };

  const handleAddIp = () => {
    const ip = ipInput.trim();
    if (!ip) return;

    // Basic validation for IP address or CIDR
    const ipRegex = /^(\d{1,3}\.){3}\d{1,3}(\/\d{1,2})?$/;
    if (!ipRegex.test(ip)) {
      setSnackbar({ open: true, message: 'Invalid IP address format', severity: 'error' });
      return;
    }

    if (!formData.ipWhitelist.includes(ip)) {
      setFormData({ ...formData, ipWhitelist: [...formData.ipWhitelist, ip] });
    }
    setIpInput('');
  };

  const handleRemoveIp = (ip) => {
    setFormData({
      ...formData,
      ipWhitelist: formData.ipWhitelist.filter((item) => item !== ip),
    });
  };

  const handleCopyToClipboard = (text) => {
    navigator.clipboard.writeText(text);
    setSnackbar({ open: true, message: 'Copied to clipboard', severity: 'success' });
  };

  return (
    <Box component="section" id="api-integrations-page" sx={{ p: 3 }}>
      <BrowserTitle title="API Integrations | Airmaxx Pro" />

      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Typography variant="h4" component="h1" sx={{ fontWeight: 600, color: '#0D3B59' }}>
          API Integrations
        </Typography>
        <Button
          variant="contained"
          startIcon={<AddIcon />}
          onClick={() => handleOpenDialog()}
          sx={{ bgcolor: '#0D3B59', '&:hover': { bgcolor: '#164B6E' } }}
        >
          Add Integration
        </Button>
      </Box>

      <Alert severity="info" sx={{ mb: 3 }}>
        Manage external API clients that can access your CRM data. Each client receives unique API credentials 
        for secure authentication. The Secret Key is only shown once when created or regenerated.
      </Alert>

      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
          <CircularProgress />
        </Box>
      ) : (
        <TableContainer component={Paper} elevation={2}>
          <Table>
            <TableHead>
              <TableRow sx={{ bgcolor: '#f5f5f5' }}>
                <TableCell sx={{ fontWeight: 600 }}>Name</TableCell>
                <TableCell sx={{ fontWeight: 600 }}>API Key</TableCell>
                <TableCell sx={{ fontWeight: 600 }}>Status</TableCell>
                <TableCell sx={{ fontWeight: 600 }}>IP Whitelist</TableCell>
                <TableCell sx={{ fontWeight: 600 }}>Scopes</TableCell>
                <TableCell sx={{ fontWeight: 600 }}>Requests</TableCell>
                <TableCell sx={{ fontWeight: 600 }}>Last Used</TableCell>
                <TableCell sx={{ fontWeight: 600 }} align="right">Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {clients.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={8} align="center" sx={{ py: 4 }}>
                    <Typography color="text.secondary">No API integrations configured</Typography>
                  </TableCell>
                </TableRow>
              ) : (
                clients.map((client) => (
                  <TableRow key={client.id} hover>
                    <TableCell>
                      <Box>
                        <Typography variant="body1" fontWeight={500}>{client.name}</Typography>
                        {client.description && (
                          <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
                            {client.description}
                          </Typography>
                        )}
                      </Box>
                    </TableCell>
                    <TableCell>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <Typography variant="body2" sx={{ fontFamily: 'monospace', fontSize: '0.85rem' }}>
                          {client.apiKey?.substring(0, 20)}...
                        </Typography>
                        <Tooltip title="Copy API Key">
                          <IconButton size="small" onClick={() => handleCopyToClipboard(client.apiKey)}>
                            <CopyIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      </Box>
                    </TableCell>
                    <TableCell>
                      <Chip
                        icon={client.isActive ? <ActiveIcon /> : <InactiveIcon />}
                        label={client.isActive ? 'Active' : 'Disabled'}
                        color={client.isActive ? 'success' : 'default'}
                        size="small"
                        onClick={() => handleToggleActive(client)}
                        sx={{ cursor: 'pointer' }}
                      />
                    </TableCell>
                    <TableCell>
                      {client.ipWhitelist?.length > 0 ? (
                        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                          {client.ipWhitelist.slice(0, 2).map((ip) => (
                            <Chip key={ip} label={ip} size="small" variant="outlined" />
                          ))}
                          {client.ipWhitelist.length > 2 && (
                            <Chip label={`+${client.ipWhitelist.length - 2}`} size="small" variant="outlined" />
                          )}
                        </Box>
                      ) : (
                        <Typography variant="body2" color="text.secondary">Any IP</Typography>
                      )}
                    </TableCell>
                    <TableCell>
                      {client.scopes?.length > 0 ? (
                        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                          {client.scopes.slice(0, 2).map((scope) => (
                            <Chip key={scope} label={scope} size="small" color="primary" variant="outlined" />
                          ))}
                          {client.scopes.length > 2 && (
                            <Chip label={`+${client.scopes.length - 2}`} size="small" color="primary" variant="outlined" />
                          )}
                        </Box>
                      ) : (
                        <Typography variant="body2" color="text.secondary">No scopes</Typography>
                      )}
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2">{client.requestCount?.toLocaleString() || 0}</Typography>
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2" color="text.secondary">
                        {formatDate(client.lastUsedAt)}
                      </Typography>
                    </TableCell>
                    <TableCell align="right">
                      <Tooltip title="Regenerate Keys">
                        <IconButton onClick={() => handleRegenerateKeys(client)} color="warning">
                          <RefreshIcon />
                        </IconButton>
                      </Tooltip>
                      <Tooltip title="Edit">
                        <IconButton onClick={() => handleOpenDialog(client)} color="primary">
                          <EditIcon />
                        </IconButton>
                      </Tooltip>
                      <Tooltip title="Delete">
                        <IconButton onClick={() => handleDelete(client)} color="error">
                          <DeleteIcon />
                        </IconButton>
                      </Tooltip>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </TableContainer>
      )}

      {/* Create/Edit Dialog */}
      <Dialog open={dialogOpen} onClose={handleCloseDialog} maxWidth="md" fullWidth>
        <DialogTitle>
          {editingClient ? 'Edit API Client' : 'Create API Client'}
        </DialogTitle>
        <DialogContent>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 1 }}>
            <TextField
              label="Name"
              value={formData.name}
              onChange={(e) => setFormData({ ...formData, name: e.target.value })}
              required
              fullWidth
              placeholder="e.g., Warehouse System"
            />
            <TextField
              label="Description"
              value={formData.description}
              onChange={(e) => setFormData({ ...formData, description: e.target.value })}
              fullWidth
              multiline
              rows={2}
              placeholder="Brief description of this integration"
            />

            <Box>
              <Typography variant="subtitle2" sx={{ mb: 1 }}>IP Whitelist</Typography>
              <Box sx={{ display: 'flex', gap: 1, mb: 1 }}>
                <TextField
                  value={ipInput}
                  onChange={(e) => setIpInput(e.target.value)}
                  placeholder="IP address or CIDR (e.g., 192.168.1.1 or 10.0.0.0/24)"
                  size="small"
                  fullWidth
                  onKeyPress={(e) => e.key === 'Enter' && handleAddIp()}
                />
                <Button variant="outlined" onClick={handleAddIp}>Add</Button>
              </Box>
              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                {formData.ipWhitelist.map((ip) => (
                  <Chip
                    key={ip}
                    label={ip}
                    onDelete={() => handleRemoveIp(ip)}
                    size="small"
                  />
                ))}
                {formData.ipWhitelist.length === 0 && (
                  <Typography variant="body2" color="text.secondary">
                    No IP restrictions (allow all)
                  </Typography>
                )}
              </Box>
            </Box>

            <Autocomplete
              multiple
              options={availableScopes.map((s) => s.scope)}
              value={formData.scopes}
              onChange={(_, newValue) => setFormData({ ...formData, scopes: newValue })}
              renderInput={(params) => (
                <TextField
                  {...params}
                  label="Scopes"
                  placeholder="Select permissions"
                />
              )}
              renderOption={(props, option) => {
                const scope = availableScopes.find((s) => s.scope === option);
                return (
                  <li {...props}>
                    <Box>
                      <Typography variant="body2">{option}</Typography>
                      <Typography variant="caption" color="text.secondary">
                        {scope?.description}
                      </Typography>
                    </Box>
                  </li>
                );
              }}
              renderTags={(value, getTagProps) =>
                value.map((option, index) => (
                  <Chip
                    {...getTagProps({ index })}
                    key={option}
                    label={option}
                    size="small"
                    color="primary"
                    variant="outlined"
                  />
                ))
              }
            />

            <FormControlLabel
              control={
                <Switch
                  checked={formData.isActive}
                  onChange={(e) => setFormData({ ...formData, isActive: e.target.checked })}
                />
              }
              label="Active"
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDialog}>Cancel</Button>
          <Button onClick={handleSubmit} variant="contained" sx={{ bgcolor: '#0D3B59' }}>
            {editingClient ? 'Update' : 'Create'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Keys Display Dialog */}
      <Dialog open={keysDialogOpen} onClose={() => setKeysDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <KeyIcon color="warning" />
          API Credentials
        </DialogTitle>
        <DialogContent>
          <Alert severity="warning" sx={{ mb: 2 }}>
            <strong>Important:</strong> The Secret Key will only be shown once. Please copy and store it securely.
          </Alert>

          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
            <Box>
              <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 0.5 }}>
                API Key
              </Typography>
              <TextField
                value={generatedKeys.apiKey}
                fullWidth
                InputProps={{
                  readOnly: true,
                  sx: { fontFamily: 'monospace', fontSize: '0.9rem' },
                  endAdornment: (
                    <InputAdornment position="end">
                      <IconButton onClick={() => handleCopyToClipboard(generatedKeys.apiKey)}>
                        <CopyIcon />
                      </IconButton>
                    </InputAdornment>
                  ),
                }}
              />
            </Box>

            <Box>
              <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 0.5 }}>
                Secret Key
              </Typography>
              <TextField
                value={showSecretKey ? generatedKeys.secretKey : '•'.repeat(64)}
                fullWidth
                InputProps={{
                  readOnly: true,
                  sx: { fontFamily: 'monospace', fontSize: '0.9rem' },
                  endAdornment: (
                    <InputAdornment position="end">
                      <IconButton onClick={() => setShowSecretKey(!showSecretKey)}>
                        {showSecretKey ? <VisibilityOffIcon /> : <VisibilityIcon />}
                      </IconButton>
                      <IconButton onClick={() => handleCopyToClipboard(generatedKeys.secretKey)}>
                        <CopyIcon />
                      </IconButton>
                    </InputAdornment>
                  ),
                }}
              />
            </Box>

            <Alert severity="info" sx={{ mt: 1 }}>
              Use these credentials in the <code>X-API-Key</code> and <code>X-API-Secret</code> headers when making API requests.
            </Alert>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => {
              setKeysDialogOpen(false);
              setShowSecretKey(false);
              setGeneratedKeys({ apiKey: '', secretKey: '' });
            }}
            variant="contained"
          >
            Done
          </Button>
        </DialogActions>
      </Dialog>

      {/* Snackbar */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={4000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      >
        <Alert
          onClose={() => setSnackbar({ ...snackbar, open: false })}
          severity={snackbar.severity}
          variant="filled"
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default ApiIntegrations;
